import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CornerUpLeft, FileText, Film, Image as ImageIcon, Loader2, Mic, Music, Paperclip, Pencil, Send, Smile, Trash2, X } from 'lucide-react';
import EmojiPicker from './EmojiPicker';
import './chat.css';

const MAX_SIZE = 25 * 1024 * 1024;
const sizeText = bytes => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const MAX_RECORDING_SECONDS = 300;

const ATTACH_OPTIONS = [
    { kind: 'image', label: 'Photo', Icon: ImageIcon, accept: 'image/*' },
    { kind: 'video', label: 'Video', Icon: Film, accept: 'video/*' },
    { kind: 'audio', label: 'Audio', Icon: Music, accept: 'audio/*' },
    { kind: 'document', label: 'File', Icon: FileText, accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip' },
];

const RECORDER_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
const pickRecorderType = () => (typeof MediaRecorder !== 'undefined' ? RECORDER_TYPES.find(t => MediaRecorder.isTypeSupported(t)) : undefined);
const extensionFor = (mime = '') => (mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm');
const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Message box with an emoji panel, an attach menu (photo / video / audio / file) and voice messages.
 *
 *  onSend({ text, attachment })  -> resolve true when sent (the box then clears), false to keep the draft
 *  uploadFile(file)              -> resolve the uploaded attachment descriptor
 *  onTyping()                    -> optional, called while the user types
 *  onError(message)              -> optional, for upload / microphone problems
 *  fileBase                      -> server origin used to preview an uploaded file
 *  replyTo / replyLabel / onCancelReply -> a message being replied to (quoted above the box)
 *  editing / onCancelEdit        -> a message being edited; onSend then receives { text, editing }
 */
const ChatComposer = ({ onSend, uploadFile, onTyping, onError, fileBase = '', placeholder = 'Type a message…', disabled = false, replyTo, replyLabel, onCancelReply, editing, onCancelEdit }) => {
    const [text, setText] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);       // 0-100, how much of the file has been sent
    const [uploadName, setUploadName] = useState('');
    // uploads a file and keeps `progress` in step with the bytes that have really left the device
    const runUpload = useCallback((file) => { setProgress(0); setUploadName(file.name || ''); return uploadFile(file, pct => setProgress(pct)); }, [uploadFile]);
    const [sending, setSending] = useState(false);
    const [panel, setPanel] = useState(null); // 'emoji' | 'attach' | null
    const [accept, setAccept] = useState('');
    const [recording, setRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);

    const rootRef = useRef(null);
    const textRef = useRef(null);
    const fileRef = useRef(null);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const discardRef = useRef(false);

    // Close the popovers on an outside click / Escape.
    useEffect(() => {
        if (!panel) return undefined;
        const onDown = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setPanel(null); };
        const onKey = e => { if (e.key === 'Escape') setPanel(null); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [panel]);

    useEffect(() => {
        if (editing) { setText(editing.text || ''); setAttachment(null); }
        if (editing || replyTo) setTimeout(() => textRef.current?.focus(), 0);
    }, [editing, replyTo]);

    // Grow the box with the text (up to a few lines).
    useEffect(() => {
        const el = textRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
    }, [text]);

    const stopStream = () => {
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        clearInterval(timerRef.current);
    };

    // Release the microphone if the chat is closed mid-recording.
    useEffect(() => () => {
        discardRef.current = true;
        if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
        stopStream();
    }, []);

    const insertEmoji = (emoji) => {
        const el = textRef.current;
        const start = el?.selectionStart ?? text.length;
        const end = el?.selectionEnd ?? text.length;
        setText(text.slice(0, start) + emoji + text.slice(end));
        onTyping?.();
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + emoji.length, start + emoji.length);
        });
    };

    const chooseFile = (option) => {
        setPanel(null);
        setAccept(option.accept);
        // The accept filter must be on the input before the dialog opens.
        setTimeout(() => fileRef.current?.click(), 0);
    };

    const onFileChosen = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (file.size > MAX_SIZE) { onError?.(`${file.name} is ${sizeText(file.size)}. The maximum for a chat file is 25 MB. Please choose a smaller file.`); return; }
        setUploading(true);
        try {
            setAttachment(await runUpload(file));
        } catch (err) {
            onError?.((err?.response?.status === 413 || (!err?.response && navigator.onLine !== false && file.size > 1024 * 1024))
                ? `413 Content Too Large: ${file.name} (${sizeText(file.size)}) is too large for the server. The maximum for a chat file is 25 MB.`
                : (err?.response?.data?.message || (err?.response ? 'Upload failed. Please try again.' : 'The upload could not reach the server. Please check your internet connection and try again.')));
        } finally {
            setUploading(false);
        }
    };

    const editMode = Boolean(editing);
    const canSend = (editMode ? text.trim() : (text.trim() || attachment)) && !uploading && !sending && !disabled;

    const send = async () => {
        if (!canSend) return;
        setSending(true);
        try {
            const ok = await onSend({ text: text.trim(), attachment, editing });
            if (ok !== false) { setText(''); setAttachment(null); setPanel(null); if (editMode) onCancelEdit?.(); }
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
        if (e.key === 'Escape' && editMode) { setText(''); onCancelEdit?.(); }
    };

    // ── Voice messages ──
    const finishRecording = useCallback(async (mimeType) => {
        stopStream();
        setRecording(false);
        const chunks = chunksRef.current;
        chunksRef.current = [];
        if (discardRef.current || !chunks.length) return;

        const type = mimeType || chunks[0].type || 'audio/webm';
        const file = new File([new Blob(chunks, { type })], `voice-message-${Date.now()}.${extensionFor(type)}`, { type });
        if (file.size > MAX_SIZE) { onError?.(`This voice message is ${sizeText(file.size)}. The maximum is 25 MB. Please record a shorter one.`); return; }

        setUploading(true);
        try {
            const uploaded = await runUpload(file);
            setSending(true);
            await onSend({ text: '', attachment: uploaded }); // a voice message sends straight away
        } catch (err) {
            onError?.(err?.response?.data?.message || err?.message || 'Could not send the voice message');
        } finally {
            setUploading(false);
            setSending(false);
        }
    }, [onError, onSend, runUpload]);

    const startRecording = async () => {
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            onError?.('Voice messages are not supported in this browser');
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickRecorderType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            chunksRef.current = [];
            discardRef.current = false;
            recorder.ondataavailable = e => { if (e.data?.size) chunksRef.current.push(e.data); };
            recorder.onstop = () => finishRecording(recorder.mimeType);
            recorder.start();
            recorderRef.current = recorder;
            setPanel(null);
            setSeconds(0);
            setRecording(true);
            timerRef.current = setInterval(() => {
                setSeconds(s => {
                    if (s + 1 >= MAX_RECORDING_SECONDS && recorderRef.current?.state === 'recording') recorderRef.current.stop();
                    return s + 1;
                });
            }, 1000);
        } catch (err) {
            stopStream();
            onError?.(err?.name === 'NotAllowedError' ? 'Microphone access was blocked — allow it in the browser to send voice messages' : 'Could not start recording');
        }
    };

    const stopRecording = (discard) => {
        discardRef.current = discard;
        if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
        else { stopStream(); setRecording(false); }
    };

    const previewSrc = attachment?.url?.startsWith('/') ? `${fileBase}${attachment.url}` : attachment?.url;
    const showMic = !editMode && !text.trim() && !attachment && !uploading;

    if (recording) {
        return (
            <div className="chat-composer" ref={rootRef}>
                <div className="chat-recorder">
                    <button type="button" className="chat-recorder__cancel" onClick={() => stopRecording(true)} aria-label="Cancel recording" title="Cancel"><Trash2 size={18} /></button>
                    <span className="chat-recorder__dot" />
                    <span className="chat-recorder__time">{clock(seconds)}</span>
                    <span className="chat-recorder__hint">Recording… tap send when done</span>
                    <button type="button" className="chat-composer__send" onClick={() => stopRecording(false)} aria-label="Send voice message" title="Send"><Send size={18} /></button>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-composer" ref={rootRef}>
            {(editMode || replyTo) && (
                <div className="chat-composer__context">
                    <span className="chat-composer__context-icon">{editMode ? <Pencil size={15} /> : <CornerUpLeft size={15} />}</span>
                    <span className="chat-composer__context-text">
                        <b>{editMode ? 'Editing message' : `Replying to ${replyLabel || ''}`}</b>
                        <span>{(editMode ? editing.text : replyTo.text) || (replyTo?.attachment?.kind ? 'Attachment' : '')}</span>
                    </span>
                    <button type="button" onClick={() => { if (editMode) { setText(''); onCancelEdit?.(); } else onCancelReply?.(); }} aria-label="Cancel"><X size={14} /></button>
                </div>
            )}

            {(attachment || uploading) && (
                <div className="chat-composer__preview">
                    {uploading ? (
                        <span className="chat-composer__uploading" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                            <Loader2 size={16} />
                            <span className="chat-composer__uploadinfo">
                                <span className="chat-composer__uploadtext">{progress >= 100 ? 'Processing…' : `Uploading ${uploadName ? uploadName.slice(0, 28) : 'file'}…`}<b>{progress}%</b></span>
                                <span className="chat-composer__bar"><i style={{ width: `${progress}%` }} /></span>
                            </span>
                        </span>
                    ) : (
                        <>
                            {attachment.kind === 'image'
                                ? <img src={previewSrc} alt="" />
                                : <span className="chat-composer__fileicon">{attachment.kind === 'video' ? <Film size={18} /> : attachment.kind === 'audio' ? <Music size={18} /> : <FileText size={18} />}</span>}
                            <span className="chat-composer__filename">{attachment.name}</span>
                            <button type="button" onClick={() => setAttachment(null)} aria-label="Remove attachment"><X size={14} /></button>
                        </>
                    )}
                </div>
            )}

            <div className="chat-composer__row">
                <div className="chat-composer__tools">
                    <button type="button" className={panel === 'emoji' ? 'is-open' : ''} onClick={() => setPanel(panel === 'emoji' ? null : 'emoji')} aria-label="Add emoji" title="Emoji"><Smile size={20} /></button>
                    {!editMode && <button type="button" className={panel === 'attach' ? 'is-open' : ''} onClick={() => setPanel(panel === 'attach' ? null : 'attach')} aria-label="Attach a file" title="Attach"><Paperclip size={20} /></button>}

                    {panel === 'emoji' && <EmojiPicker onPick={insertEmoji} />}
                    {panel === 'attach' && (
                        <div className="chat-attach-menu" role="menu">
                            {ATTACH_OPTIONS.map(o => (
                                <button key={o.kind} type="button" role="menuitem" onClick={() => chooseFile(o)}>
                                    <span className={`chat-attach-menu__icon is-${o.kind}`}><o.Icon size={16} /></span> {o.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <textarea
                    ref={textRef}
                    rows={1}
                    value={text}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={e => { setText(e.target.value); onTyping?.(); }}
                    onKeyDown={onKeyDown}
                />

                {showMic ? (
                    <button type="button" className="chat-composer__send chat-composer__mic" onClick={startRecording} disabled={disabled || sending} aria-label="Record a voice message" title="Voice message"><Mic size={18} /></button>
                ) : (
                    <button type="button" className="chat-composer__send" onClick={send} disabled={!canSend} aria-label="Send message">
                        {sending ? <Loader2 size={18} /> : <Send size={18} />}
                    </button>
                )}
            </div>

            <input ref={fileRef} type="file" hidden accept={accept} onChange={onFileChosen} />
        </div>
    );
};

export default ChatComposer;
